import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/primitives";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch (e) { /* keep polling */ }
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="max-w-md text-center">
        {status === "checking" && <><Loader2 className="mx-auto text-[#0F2C59] animate-spin" size={40} /><p className="mt-4 text-slate-600">Confirming your payment…</p></>}
        {status === "paid" && <>
          <CheckCircle className="mx-auto text-[#0F2C59]" size={48} />
          <h1 className="font-serif-display text-3xl font-bold mt-5 text-[#111827]">Thank you for your purchase</h1>
          <p className="text-slate-600 mt-3">Your order is confirmed. You'll receive access details by email shortly.</p>
          <Button to="/books" className="mt-7" data-testid="payment-success-books">Back to Books</Button>
        </>}
        {status === "pending" && <>
          <Loader2 className="mx-auto text-[#0F2C59]" size={40} />
          <h1 className="font-serif-display text-2xl font-bold mt-5">Payment is processing</h1>
          <p className="text-slate-600 mt-3">This can take a moment. We'll email you once it's confirmed.</p>
          <Button to="/" variant="secondary" className="mt-7">Home</Button>
        </>}
        {status === "error" && <>
          <XCircle className="mx-auto text-red-500" size={48} />
          <h1 className="font-serif-display text-2xl font-bold mt-5">We couldn't confirm this payment</h1>
          <Button to="/books" className="mt-7">Back to Books</Button>
        </>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="max-w-md text-center">
        <XCircle className="mx-auto text-slate-400" size={48} />
        <h1 className="font-serif-display text-3xl font-bold mt-5 text-[#111827]">Checkout cancelled</h1>
        <p className="text-slate-600 mt-3">No charge was made. You can return whenever you're ready.</p>
        <div className="flex gap-3 justify-center mt-7">
          <Button to="/books">Back to Books</Button>
          <Button to="/" variant="secondary">Home</Button>
        </div>
      </div>
    </div>
  );
}
